import { motion } from "framer-motion";
import { CalendarDays, Search, FileText, Compass, Wrench, Activity, TrendingUp, Check } from "lucide-react";
import { Reveal, Label } from "./Motion";
import ChapterMark from "./ChapterMark";

const STEPS = [
  { n: "01", icon: CalendarDays, title: "Discovery", body: "A 30-minute conversation to understand the business, goals, challenges, and priorities." },
  { n: "02", icon: Search, title: "Business Assessment", body: "We analyze the information available to identify patterns, problems, opportunities, and constraints." },
  { n: "03", icon: FileText, title: "Elevare Report", body: "We present our findings and identify the highest-impact opportunities." },
  { n: "04", icon: Compass, title: "Strategy", body: "We build a prioritized improvement roadmap." },
  { n: "05", icon: Wrench, title: "Implementation", body: "We work alongside the business to execute the strategy." },
  { n: "06", icon: Activity, title: "Measurement", body: "We track performance and determine what is working." },
  { n: "07", icon: TrendingUp, title: "Elevation", body: "We identify the next opportunity for improvement and continue the cycle." },
];

export default function HowItWorks() {
  return (
    <section id="process" data-testid="how-it-works" className="relative py-28 lg:py-40 bg-[#eae7de]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <Reveal className="max-w-3xl">
          <ChapterMark n="02" title="Process" className="mb-5" />
          <Label>How Elevare works</Label>
          <h2 className="mt-6 font-head text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#1e3d3a]">
            A Deliberate Cycle,<br />
            <span className="text-[#1e3d3a]/45">From Discovery to Elevation.</span>
          </h2>
          <p className="mt-6 text-base lg:text-lg leading-relaxed text-[#1e3d3a]/70">
            Seven steps. A repeatable system that turns understanding into measurable improvement —
            then keeps compounding it.
          </p>
        </Reveal>

        <div className="mt-16 lg:mt-20 relative">
          {/* Vertical gold rail behind step numbers */}
          <div
            aria-hidden="true"
            className="hidden lg:block absolute left-[calc(50%-0.5px)] top-6 bottom-6 w-px bg-gradient-to-b from-[#c9a961]/0 via-[#c9a961]/50 to-[#c9a961]/0"
          />

          <div className="space-y-10 lg:space-y-24">
            {STEPS.map((s, i) => {
              const flipped = i % 2 === 1;
              return (
                <Reveal key={s.n} delay={i * 0.06}>
                  <div
                    data-testid={`step-${s.n}`}
                    className={`relative grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-center ${flipped ? "" : ""}`}
                  >
                    {/* Center node — only on lg */}
                    <motion.span
                      initial={{ scale: 0.6, opacity: 0 }}
                      whileInView={{ scale: 1, opacity: 1 }}
                      viewport={{ once: true, margin: "-40px" }}
                      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: i * 0.06 }}
                      className="hidden lg:flex absolute left-1/2 -translate-x-1/2 z-10 h-11 w-11 items-center justify-center rounded-full border border-[#c9a961] bg-[#eae7de]"
                    >
                      <s.icon className="h-4 w-4 text-[#c9a961]" strokeWidth={1.6} />
                    </motion.span>

                    {/* Left column: step number */}
                    <div className={`lg:col-span-5 ${flipped ? "lg:col-start-8 lg:text-left" : "lg:text-right"}`}>
                      <div className="flex lg:hidden items-center gap-3 mb-3">
                        <s.icon className="h-5 w-5 text-[#c9a961]" strokeWidth={1.5} />
                        <span className="text-[10px] uppercase tracking-[0.28em] text-[#c9a961] font-medium">Step {s.n}</span>
                      </div>
                      <div className={`hidden lg:flex ${flipped ? "justify-start" : "justify-end"}`}>
                        <div>
                          <span className={`block text-[10px] uppercase tracking-[0.28em] text-[#c9a961] font-medium mb-2 ${flipped ? "text-left" : "text-right"}`}>Step {s.n}</span>
                          <span className="font-head text-8xl lg:text-9xl font-light tracking-tighter outline-text leading-none">{s.n}</span>
                        </div>
                      </div>
                    </div>

                    {/* Right column: content */}
                    <div className={`lg:col-span-5 ${flipped ? "lg:col-start-2 lg:row-start-1" : "lg:col-start-8"}`}>
                      <div className="rounded-3xl border border-[#1e3d3a]/12 bg-[#f4f1e8] p-7 lg:p-8 shadow-[0_2px_0_rgba(30,61,58,0.03)]">
                        <h3 className="font-head text-2xl lg:text-3xl tracking-tight text-[#1e3d3a]">{s.title}</h3>
                        {s.timing && <p className="mt-2 text-[11px] uppercase tracking-[0.25em] text-[#1e3d3a]/55">{s.timing}</p>}
                        <p className="mt-5 text-sm lg:text-base leading-relaxed text-[#1e3d3a]/75">{s.body}</p>
                        {s.deliverables?.length > 0 && (
                          <ul className="mt-5 space-y-2">
                            {s.deliverables.map((d) => (
                              <li key={d} className="flex items-start gap-2.5 text-sm text-[#1e3d3a]/80">
                                <Check className="h-4 w-4 text-[#6b7c3a] shrink-0 mt-0.5" />
                                {d}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
